import { z } from 'zod/v4';

/**
 * The question and the answer request the comment is filed against.
 *
 * The legacy route took both in the **body** alongside the comment text
 * (`POST /v1/questions/comments`). They move into the path here so create
 * addresses the same collection the list read does — see
 * `ListQuestionCommentsParamsSchema`, whose path this mirrors exactly.
 *
 * The 64-character cap is the legacy body schema's.
 */
export const CreateQuestionCommentParamsSchema = z.object({
  questionId: z.string().min(1).max(64),
  requestId: z.string().min(1).max(64),
});

export type CreateQuestionCommentParams = z.infer<
  typeof CreateQuestionCommentParamsSchema
>;

/**
 * Just the comment text — the ids live on the path.
 *
 * `min(1)` and `max(4000)` are the legacy body schema's, and the legacy schema
 * was `additionalProperties: false`, so a strict object here is parity rather
 * than a tightening.
 */
export const CreateQuestionCommentBodySchema = z.object({
  questionComment: z.string().min(1).max(4000),
});

export type CreateQuestionCommentBody = z.infer<
  typeof CreateQuestionCommentBodySchema
>;

/**
 * The comment row as the INSERT returns it.
 *
 * Same projection as the legacy `queries.getById`, which the legacy service
 * called immediately after the INSERT to build its 201 body. That is
 * `QuestionCommentRowSchema` (the list row) **plus `requestId`** — the list
 * SELECT filters on `request_id` without projecting it, but `getById` does
 * project it and the legacy integration test asserts it on the create response.
 *
 * **The comment author is `updatedBy*`, not `createdBy*`.** Both frontends map
 * `commentedByUserName` / `commentedByCompanyName` off the `updatedBy` columns,
 * because the INSERT writes the whole audit block — `updated_by_*` included — on
 * create. `createdByUserName` is deliberately absent: the legacy
 * `auditInsertDBColumns()` helper omits that one column, so it has never been
 * written and is null on every row that exists.
 *
 * `looseObject` because the legacy response schema was
 * `additionalProperties: true`, so the remaining audit columns (`createdByUserId`,
 * `createdByCompanyId`, the api-key pair, the impersonate pair) ride out to the
 * wire untouched. Date columns are left undeclared so they pass through as `Date`
 * and serialise to ISO strings — the convention the rest of this module's schemas
 * set.
 */
export const CreatedQuestionCommentSchema = z.looseObject({
  id: z.string(),
  questionId: z.string().nullable().optional(),
  requestId: z.string().nullable().optional(),
  questionComment: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
});

export type CreatedQuestionComment = z.infer<
  typeof CreatedQuestionCommentSchema
>;

/**
 * `{ metadata, data }` — `reply.apiSuccess(comment, 201)`, the convention every
 * other migrated create route in this module answers in (see
 * `CreateQuestionnaireContract`).
 *
 * This is a deliberate change from the legacy route, which sent the comment as a
 * bare top-level object. `frontend-v2` discards the create response entirely (it
 * invalidates the thread query instead), so only the old frontend's
 * `useComment` hook reads the body, and it is updated in the same change.
 */
export const CreateQuestionCommentResponseSchema = z.object({
  metadata: z.object({ success: z.boolean() }),
  data: CreatedQuestionCommentSchema,
});

export type CreateQuestionCommentResponse = z.infer<
  typeof CreateQuestionCommentResponseSchema
>;
